import type { ThumbnailSource } from "@/src/_types/Article/feature-image-thumbnail";

export const AI_IMAGE_CREDIT = "AI-generated illustration";

type CreditThumbnail = {
  source: ThumbnailSource;
  image_credit?: string | null;
};

type CreditJournal = {
  title?: string | null;
  meta_data?: { nlm_ta?: string | null } | null;
} | null;

const asThumbnail = (thumbnail: unknown): CreditThumbnail | null =>
  typeof thumbnail === "object" &&
  thumbnail !== null &&
  !Array.isArray(thumbnail) &&
  typeof (thumbnail as { source?: unknown }).source === "string"
    ? (thumbnail as CreditThumbnail)
    : null;

export const isAiGeneratedThumbnail = (thumbnail: unknown): boolean =>
  asThumbnail(thumbnail)?.source === "ai";

export const withAiDisclosure = (text: string, thumbnail: unknown): string =>
  isAiGeneratedThumbnail(thumbnail) ? `${text} (${AI_IMAGE_CREDIT})` : text;

export const imageCredit = (
  thumbnail: unknown,
  journal: CreditJournal,
): string => {
  const journalCredit = journal?.meta_data?.nlm_ta || journal?.title || "";
  const current = asThumbnail(thumbnail);

  if (current?.source === "ai") {
    return AI_IMAGE_CREDIT;
  }
  if (current?.source === "manual") {
    return current.image_credit?.trim() || journalCredit;
  }

  return journalCredit;
};

export type CreditPart =
  | { kind: "text"; value: string }
  | { kind: "link"; href: string };

const CREDIT_URL = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

export const splitCreditLinks = (credit: string): CreditPart[] => {
  const parts: CreditPart[] = [];
  let cursor = 0;

  for (const match of credit.matchAll(CREDIT_URL)) {
    const url = match[0].replace(TRAILING_PUNCTUATION, "");
    const start = match.index;
    if (start > cursor) {
      parts.push({ kind: "text", value: credit.slice(cursor, start) });
    }
    parts.push({
      kind: "link",
      href: /^https?:\/\//i.test(url) ? url : `https://${url}`,
    });
    cursor = start + url.length;
  }

  if (cursor < credit.length) {
    parts.push({ kind: "text", value: credit.slice(cursor) });
  }

  return parts;
};
